import { useContext, useEffect } from 'react';

import { CalendarInteractive } from '../../../../../components/calendar-interactive/calendar-interactive';
import { RangeEnum } from '../../../../../model/range.enum';
import { BacktestingContext } from '../../../../../providers/backtesting/backtesting-provider';
import { BacktestHeaderErrors } from '../../../../../providers/backtesting/types';
import { BacktestingInput } from '../../strategy-form/components/backtesting-input';
import { BacktestingSelect } from '../../strategy-form/components/backtesting-select';

import './specification-global-config.scss';

const TOKEN_OPTIONS = [
  { name: 'BTC', value: 'BTC' },
  { name: 'ETH', value: 'ETH' },
  { name: 'SOL', value: 'SOL' },
];

const BACKTEST_PERIOD_OPTIONS = [
  { label: 'Past 7 days', value: RangeEnum.W1 },
  { label: 'Past 1 month', value: RangeEnum.M1 },
  { label: 'Past 6 months', value: RangeEnum.M6 },
  { label: 'Past 1 year', value: RangeEnum.Y1 },
  { label: 'Past 2 years', value: RangeEnum.Y2 },
  { label: 'Past 3 years', value: RangeEnum.Y3 },
];

// Backtest-global config (Token / Backtest Period / Frequency) — shared by all
// stratlets. Relocated from the old page-level BacktestHeader to the top of the
// Specification tab, where the backtest is configured. Keeps the exact wiring:
// updateGlobalConfig on edit, the same run-time validation trigger registered
// via registerBacktestHeader (runBacktest calls it), and headerErrors display.
export const SpecificationGlobalConfig = () => {
  const {
    activeBacktestId,
    clearBacktestHeaderError,
    getActiveBacktest,
    registerBacktestHeader,
    setBacktestHeaderErrors,
    unregisterBacktestHeader,
    updateGlobalConfig,
  } = useContext(BacktestingContext);

  const activeBacktest = getActiveBacktest();

  useEffect(() => {
    if (!activeBacktest || !activeBacktestId) return;
    const { globalConfig } = activeBacktest;
    registerBacktestHeader(activeBacktestId, {
      trigger: async () => {
        const nextErrors: BacktestHeaderErrors = {};

        if (!globalConfig.token) {
          nextErrors.token = 'Token is required';
        }

        const { start, end } = globalConfig.backtestPeriod;
        if (!start || !end) {
          nextErrors.backtestPeriod = 'Backtest period is required';
        } else if (start > end) {
          nextErrors.backtestPeriod = 'Start date must be before end date';
        }

        if (!globalConfig.frequency || globalConfig.frequency <= 0) {
          nextErrors.frequency = 'Required';
        }

        setBacktestHeaderErrors(activeBacktestId, nextErrors);
        return Object.keys(nextErrors).length === 0;
      },
    });

    return () => unregisterBacktestHeader(activeBacktestId);
  }, [
    activeBacktest,
    activeBacktestId,
    registerBacktestHeader,
    setBacktestHeaderErrors,
    unregisterBacktestHeader,
  ]);

  if (!activeBacktest || !activeBacktestId) return null;

  const { globalConfig, headerErrors } = activeBacktest;

  return (
    <div className="specification-global-config">
      <div className="specification-global-config-header">
        <span className="specification-global-config-title">Backtest configuration</span>
      </div>
      <div className="specification-global-config-fields">
        <div className="specification-global-config-field">
          <label className="specification-global-config-label">Token</label>
          <BacktestingSelect
            values={TOKEN_OPTIONS}
            defaultValue={
              TOKEN_OPTIONS.find((option) => option.value === globalConfig.token) ??
              TOKEN_OPTIONS[0]
            }
            errors={headerErrors.token}
            onChange={(value) => {
              updateGlobalConfig(activeBacktestId, { token: value });
              clearBacktestHeaderError(activeBacktestId, 'token');
            }}
            className="specification-global-config-select"
          />
        </div>

        <div
          className={`specification-global-config-field specification-global-config-field-period ${
            headerErrors.backtestPeriod ? 'specification-global-config-field-error' : ''
          }`}
        >
          <CalendarInteractive
            value={globalConfig.backtestPeriod}
            label="Backtest Period"
            periodOptions={BACKTEST_PERIOD_OPTIONS}
            selectedRange={globalConfig.backtestPeriodRange}
            onApply={(startDate, endDate, range) => {
              updateGlobalConfig(activeBacktestId, {
                backtestPeriod: {
                  start: startDate ? new Date(startDate) : null,
                  end: endDate ? new Date(endDate) : null,
                },
                backtestPeriodRange: range ?? undefined,
              });
              clearBacktestHeaderError(activeBacktestId, 'backtestPeriod');
            }}
          />
          {headerErrors.backtestPeriod && (
            <div className="specification-global-config-error">{headerErrors.backtestPeriod}</div>
          )}
        </div>

        <div className="specification-global-config-field">
          <BacktestingInput
            placeholder="e.g. 15"
            className="specification-global-config-frequency-input"
            label="Frequency"
            errors={headerErrors.frequency}
            value={globalConfig.frequency?.toString() ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value;
              updateGlobalConfig(activeBacktestId, {
                frequency: val ? Number(val) : null,
              });
              clearBacktestHeaderError(activeBacktestId, 'frequency');
            }}
          />
        </div>
      </div>
    </div>
  );
};
